import { useState } from "react";
import { motion } from "framer-motion";
import { Mail, Lock, ArrowRight, Loader2, Eye, EyeOff, Brain, Target, Users, LineChart } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const FEATURES = [
  { icon: Brain, title: "Financial Acumen", desc: "Build real money confidence" },
  { icon: Target, title: "Tailored Learning", desc: "Learnings shaped to each business" },
  { icon: Users, title: "Client Insights", desc: "See where each client stands" },
  { icon: LineChart, title: "Progress Tracking", desc: "Watch their skills grow" },
];

export default function Login() {
  const { login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email.trim(), password);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Login failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-2">
      {/* Left panel */}
      <div className="relative hidden lg:flex flex-col justify-center px-14 xl:px-20 bg-white">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ type: "spring", stiffness: 200, damping: 22 }}
        >
          <img src="/eyb-logo.png" alt="EYB" className="w-16 h-16 mb-8" />
          <h1 className="font-display text-5xl font-bold text-[#0F2A3F] leading-none">EYB Consulting</h1>
          <p className="text-slate-400 text-lg font-medium mt-4 max-w-sm leading-relaxed">
            Clarity and control, built for your business.
          </p>

          <div className="mt-12 space-y-6 max-w-md">
            {FEATURES.map((f, i) => (
              <motion.div
                key={f.title}
                initial={{ opacity: 0, x: -16 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.2 + i * 0.1 }}
                className="flex items-center gap-4 pl-4 border-l-2 border-sky-100"
              >
                <div className="w-11 h-11 rounded-xl bg-sky-50 flex items-center justify-center shrink-0">
                  <f.icon className="w-5 h-5 text-[#29ABE2]" strokeWidth={2.4} />
                </div>
                <div>
                  <div className="font-bold text-[#0F2A3F]">{f.title}</div>
                  <div className="text-sm text-slate-400 font-medium">{f.desc}</div>
                </div>
              </motion.div>
            ))}
          </div>

          <p className="mt-14 text-[11px] uppercase tracking-[0.2em] font-bold text-slate-300">
            Secure Learnings Portal
          </p>
        </motion.div>
      </div>

      {/* Right panel */}
      <div className="relative flex items-center justify-center px-6 sm:px-14 bg-[#0F2A3F]">
        <div className="pointer-events-none absolute inset-0 opacity-30"
             style={{ background: "radial-gradient(circle at 70% 30%, #1E3A52 0%, transparent 55%)" }} />
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ type: "spring", stiffness: 200, damping: 22, delay: 0.1 }}
          className="relative w-full max-w-md"
        >
          <div className="lg:hidden mb-8 flex items-center gap-3">
            <img src="/eyb-logo.png" alt="EYB" className="w-10 h-10" />
            <span className="font-display text-2xl font-bold text-white">EYB Consulting</span>
          </div>

          <h2 className="font-display text-4xl font-bold text-white">Welcome</h2>
          <p className="text-slate-400 font-medium mt-2 mb-8">Sign in to access your learnings portal</p>

          <form onSubmit={submit} className="space-y-5">
            <div>
              <label className="block text-[11px] uppercase tracking-[0.12em] font-bold text-slate-400 mb-2">Email address</label>
              <div className="relative">
                <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
                <input
                  data-testid="login-email-input"
                  type="email"
                  autoFocus
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full rounded-xl bg-[#17344B] border border-[#254865] focus:border-[#29ABE2] outline-none pl-12 pr-4 py-3.5 font-medium text-white placeholder-slate-500 transition-colors"
                />
              </div>
            </div>

            <div>
              <label className="block text-[11px] uppercase tracking-[0.12em] font-bold text-slate-400 mb-2">Password</label>
              <div className="relative">
                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
                <input
                  data-testid="login-password-input"
                  type={showPw ? "text" : "password"}
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full rounded-xl bg-[#17344B] border border-[#254865] focus:border-[#29ABE2] outline-none pl-12 pr-12 py-3.5 font-medium text-white placeholder-slate-500 transition-colors"
                />
                <button type="button" onClick={() => setShowPw((s) => !s)} tabIndex={-1}
                        className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300">
                  {showPw ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            {error && (
              <div data-testid="login-error" className="bg-rose-500/10 border border-rose-500/40 text-rose-300 rounded-xl px-4 py-3 text-sm font-semibold">
                {error}
              </div>
            )}

            <button
              data-testid="login-submit-btn"
              type="submit"
              disabled={loading}
              className="w-full flex items-center justify-center gap-2 text-white font-bold rounded-xl py-4 bg-[#29ABE2] hover:bg-[#1E92C7] transition-colors disabled:opacity-70"
            >
              {loading ? (
                <><Loader2 className="w-5 h-5 animate-spin" /> Signing in…</>
              ) : (
                <>Sign in <ArrowRight className="w-5 h-5" strokeWidth={2.5} /></>
              )}
            </button>
          </form>
        </motion.div>
      </div>
    </div>
  );
}
